import React from "react";
import { Truck as TruckIcon, User, CheckCircle, Warning } from "@phosphor-icons/react";

const PleinChips = ({ pleins, threshold, showPlate }) => (
  <div className="flex flex-wrap gap-1 mt-2">
    {pleins.map((p, i) => (
      <span
        key={i}
        className={`px-1.5 py-0.5 text-[10px] mono font-bold ${p.l_per_100 > threshold ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`}
        title={`${p.date.slice(0, 10)}${showPlate ? ` · ${p.license_plate}` : ` · ${p.driver || "—"}`}`}
      >
        {p.date.slice(5, 10).split("-").reverse().join("/")} · {p.l_per_100.toFixed(1)}
        {showPlate && <span className="opacity-80"> · {p.license_plate}</span>}
      </span>
    ))}
  </div>
);

const AlertCard = ({ icon: Icon, title, subtitle, a, threshold, showPlate, testId }) => (
  <div className="border-2 border-red-600 bg-white p-3" data-testid={testId}>
    <div className="flex items-center gap-2">
      <Icon size={18} weight="bold" className="text-red-600" />
      <span className="font-bold mono uppercase">{title}</span>
      <span className="text-[10px] text-zinc-500 uppercase tracking-widest">{subtitle}</span>
      <span className="ml-auto mono font-bold text-red-600 text-lg" data-testid={`${testId}-avg`}>{a.avg_l_per_100.toFixed(2)} L/100</span>
    </div>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
      Moyenne des {a.count} derniers pleins · {a.over_count} au-dessus du seuil · dernier le {a.last_date.slice(0, 10)}
    </div>
    <PleinChips pleins={a.pleins} threshold={threshold} showPlate={showPlate} />
  </div>
);

const Section = ({ title, items, render, empty }) => (
  <section className="mb-6">
    <h2 className="text-base md:text-lg font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
      {title} <span className={`mono px-2 text-sm ${items.length ? "bg-red-600 text-white" : "bg-zinc-200 text-zinc-600"}`}>{items.length}</span>
    </h2>
    {items.length === 0 ? (
      <div className="border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm p-4 flex items-center gap-2">
        <CheckCircle size={18} weight="fill" /> {empty}
      </div>
    ) : (
      <div className="grid md:grid-cols-2 gap-3">{items.map(render)}</div>
    )}
  </section>
);

export const FuelAlertsPanel = ({ alerts, threshold }) => {
  if (!alerts) return null;
  return (
    <div data-testid="fuel-alerts-panel">
      <div className="border-l-4 border-red-600 bg-red-50 px-4 py-3 mb-6 text-sm flex items-start gap-2">
        <Warning size={18} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
        <div>
          Un camion ou un chauffeur est en alerte si la moyenne de ses <b>{alerts.last_n} derniers pleins</b> dépasse <b>{threshold} L/100</b>.
          Les pleins aberrants sont ignorés. Dès que les pleins suivants redeviennent normaux, l'alerte disparaît d'elle-même.
        </div>
      </div>
      <Section
        title="Camions en surconsommation"
        items={alerts.trucks}
        empty="Aucun camion en surconsommation sur ses derniers pleins."
        render={(a) => (
          <AlertCard key={a.truck_id} icon={TruckIcon} title={a.license_plate} subtitle={`${a.brand} · ${a.site}`} a={a}
            threshold={threshold} testId={`fuel-alert-truck-${a.license_plate}`} />
        )}
      />
      <Section
        title="Chauffeurs en surconsommation"
        items={alerts.drivers}
        empty="Aucun chauffeur en surconsommation sur ses derniers pleins."
        render={(a) => (
          <AlertCard key={a.name} icon={User} title={a.name} subtitle="" a={a} threshold={threshold} showPlate
            testId={`fuel-alert-driver-${a.name}`} />
        )}
      />
    </div>
  );
};
